import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbDateParserFormatter, NgbTimeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardHeaderComponent,
  CheckboxComponent,
  DatepickerComponent,
  FormGroupComponent,
  MbsSize,
  NumberComponent,
  RadioComponent,
  RadioDescription,
  SafePipe,
  SelectComponent,
  TimepickerComponent
} from 'mbs-ui-kit';
import { ShortDateParserFormatter } from 'mbs-ui-kit/form/datepicker/datepicker-formatters/short-date-parser-formatter';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import { EnumHelper } from 'mbs-ui-kit/utils/enum-helper';
import { FormsUtil } from 'mbs-ui-kit/utils/forms-util';
import { MbsValidators } from 'mbs-ui-kit/utils/mbs-validators';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import {
  FullBackupData,
  IncrementalData,
  OverdueAfterSuccessPeriod,
  ScheduleModalData,
  ScheduleStepValue
} from 'mbs-ui-shared@models/backup/schedule-models';
import { ScheduleType } from 'mbs-ui-shared@models/schedule-type.enum';
import { baseDateFormat, getISODateFromTimeAndDate } from 'mbs-ui-shared@utils/date';
import { stopAfterMinutesValidator } from 'mbs-ui-shared@utils/validators';
import moment from 'moment';
import { noop } from 'rxjs';
import { WizardBaseForPlanHelper } from 'wizards/helpers/bases/wizard-base-helpers';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { CalculatedPropertyRetention, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { ScheduleModalComponent } from 'wizards/steps/schedule-step/schedule-modal/schedule-modal.component';
import { ScheduleStepBase } from 'wizards/steps/Schedule.StepBase.class';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { NgbTimeStringAdapterFormat12 } from 'mbs-ui-kit/form/timepicker/ngb-time-adapters/ngb-time-string-adapter-format-12';
import { startWith } from 'rxjs/operators';
import { isSelectedDaysValidator } from './NBF/schedule-step.component.utils';

export type ScheduleMinPeriodAlert = 'simple' | 'advanced';

const ScheduleStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => ScheduleStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-schedule-step',
  templateUrl: './schedule-step.component.html',
  styleUrls: ['./schedule-step.component.scss'],
  providers: [
    ScheduleStepValueAccessor,
    { provide: NgbDateParserFormatter, useClass: ShortDateParserFormatter },
    { provide: NgbTimeAdapter, useClass: NgbTimeStringAdapterFormat12 },
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }
  ],
  imports: [
    AlertComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CheckboxComponent,
    CommonModule,
    DatepickerComponent,
    FormGroupComponent,
    HelpLinkDirective,
    I18NextPipe,
    NumberComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SafePipe,
    SelectComponent,
    TimepickerComponent
  ]
})
export class ScheduleStepComponent extends ScheduleStepBase<ScheduleStepValue> implements OnInit, OnChanges {
  private ability = inject(AbilityService);
  private injector = inject(Injector);
  stepService: WizardStepsService;
  mainService: RemoteManagementWizardsService;
  private modalService = inject(ModalService);
  protected i18nextService: I18NextService;
  private facade = inject(ComputerBackupFacade);

  @Input() stepFocused = false;
  @Input() fastNTFS: boolean;
  @Input() isGFSMode: boolean;
  @Input() retentionTitle: string;
  @Input() retentionUseDefault: boolean;
  @Output() changeType = new EventEmitter();
  @Output() offFastNTFS = new EventEmitter();
  @Output() incrementalData = new EventEmitter();
  @Output() selectStep = new EventEmitter<string>();

  public subtitleByType = this.i18nextService.t('wizards:backup');

  public invalidFFIStorageBySynthetic: boolean;
  public retentionAlerts: CalculatedPropertyRetention['alerts'] = {};
  public showOverdue = true;
  public agentTimezone = 0;
  public OverduePeriod = EnumHelper.EnumToSelectIndexesArray(OverdueAfterSuccessPeriod);

  public schedulesTexts = { incrementalData: '', fullBackupData: '' };
  public elementSelectors = {
    id: {
      fullBackupEnabledCheckbox: 'full-backup-enabled-checkbox',
      fullBackupEnabledCheckboxEdit: 'full-backup-enabled-checkbox-edit',
      minPeriodAlert: 'schedule-min-period-alert',
      scheduleTypeRadio1: 'schedule-type-radio-1',
      scheduleTypeRadio2: 'schedule-type-radio-2',
      scheduleTypeRadio3: 'schedule-type-radio-3',
      scheduleTypeRadio3Edit: 'schedule-type-radio-3-edit',
      scheduleTypeRadio4: 'schedule-type-radio-4',
      scheduleTypeRadio5: 'schedule-type-radio-5',
      scheduleTypeRadio6: 'schedule-type-radio-6',
      scheduleTypeRadio6Edit: 'schedule-type-radio-6-edit'
    }
  };

  private validVersionForSchedule = false;
  private confirmedDate = false;
  private confirmedOffNTFS = false;
  private initialScheduleType: string;

  protected readonly MbsSize = MbsSize;

  @ViewChild('fastNTFSModalContent', { static: true, read: TemplateRef }) fastNTFSModalContent: TemplateRef<HTMLElement>;

  // The same conditions RetentionPolicyControlPanelComponent applies to the alerts the NBF schedule step shows, including hiding the
  // Intelligent Retention one in Backup Templates wizards, which run without a computer
  public get minPeriodAlert(): ScheduleMinPeriodAlert {
    if (!this.isNBF || this.isRestore || this.isConsistencyCheck) return null;

    switch (this.stepForm?.get('ScheduleType').value) {
      case 'ffi':
        return this.storage.minPeriod && this.mainService.hid ? 'simple' : null;
      case 'predefined':
        return this.retentionAlerts?.needMinPeriodAlert ? 'advanced' : null;
      default:
        return null;
    }
  }

  constructor() {
    const stepService = inject(WizardStepsService);
    const mainService = inject(RemoteManagementWizardsService);
    const i18nextService = inject(I18NextService);

    super(mainService, stepService, i18nextService);
    const ability = this.ability;
    this.stepService = stepService;
    this.mainService = mainService;
    this.i18nextService = i18nextService;

    this.showOverdue = ability.can('read', 'Monitoring') && !this.isRDMode;
    this.validVersionForSchedule = this.isRDMode || mainService.validVersionForSchedule;
    toObservable(this.facade.timezoneByHid(this.mainService.hid), { injector: this.injector })
      .pipe(untilDestroyed(this))
      .subscribe({ next: (timezone) => (this.agentTimezone = timezone) });
    this.setSubtitleType();
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    super.initForm();

    this.stepForm = new UntypedFormGroup(
      {
        ScheduleType: new FormControl(''),
        specificDateGroup: new UntypedFormGroup({
          date: new FormControl(),
          time: new FormControl('00:00', MbsValidators.timeValidatorWithoutText)
        }),
        StopAfterEnabled: new FormControl(false),
        stopAfterHours: new FormControl(0),
        stopAfterMinutes: new FormControl(0, (control) => {
          if (this.stepForm?.get('StopAfterEnabled').value) {
            return stopAfterMinutesValidator.bind(this, 'stopAfterHours')(control);
          }

          return null;
        }),
        ForceMissedSchedule: new FormControl(false),
        overdueAfterSuccessEnabled: new FormControl(false),
        overdueAfterSuccessAmount: new FormControl(7, Validators.min(1)),
        overdueAfterSuccessPeriod: new FormControl(1),
        incrementalData: new UntypedFormGroup(StepsHelpers.getDefaultIncrementalSchedule(this.validVersionForSchedule)),
        fullBackupData: new UntypedFormGroup(StepsHelpers.getDefaultFullSchedule(this.validVersionForSchedule)),
        syncBeforeRun: new FormControl(false)
      },
      this.isSelectedDaysValidator.bind(this)
    );

    this.initFormEvents();

    if (!this.showOverdue) {
      this.toggleFormControls(['overdueAfterSuccessEnabled', 'overdueAfterSuccessAmount', 'overdueAfterSuccessPeriod'], false);
    }
  }

  protected initEvents(): void {
    super.initEvents();

    this.stepService
      .pipeProperty('options', 'syntheticFull')
      .pipe(untilDestroyed(this))
      .subscribe((syntheticFull) => {
        this.invalidFFIStorageBySynthetic = !syntheticFull;
      });

    this.stepService
      .pipeProperty('retention')
      .pipe(startWith(this.stepService.getSnapshot('retention')), untilDestroyed(this))
      .subscribe((retention) => {
        this.retentionAlerts = retention.alerts;

        if (retention.isGFSMode) {
          this.isSimpleDisabled = true;

          this.deactivateSimple();
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected deactivateSimple(): void {}

  protected initFormEvents(): void {
    super.initFormEvents();

    this.stepForm
      .get('specificDateGroup')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.dateTimeChangeHandler());
  }

  onStepFormChange(value: ScheduleStepValue): void {
    const onceValid = value.ScheduleType === ScheduleType[ScheduleType.once] ? this.confirmedDate : true;
    const invalid = this.isBackupPlan && value.ScheduleType === 'instantly' && this.fastNTFS && !this.confirmedOffNTFS;
    const invalidFFI = (this.invalidFFIStorage || this.invalidFFIStorageBySynthetic) && this.stepForm.get('ScheduleType').value === 'ffi';

    this.value = { ...value, oldStepLogic: true, valid: this.stepForm.valid && onceValid && !invalid && !invalidFFI };
    this.publishScheduleContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stepFocused && changes.stepFocused.currentValue) {
      this.dateTimeChangeHandler();
      setTimeout(() => this.stepForm.updateValueAndValidity());
    }

    if (this.stepForm && changes.fastNTFS && changes.fastNTFS.currentValue) {
      this.confirmedOffNTFS = false;
      queueMicrotask(() => this.stepForm.updateValueAndValidity());
    }

    if (changes.invalidFFIStorage) {
      queueMicrotask(() => this.stepForm.updateValueAndValidity());
    }
  }

  isSelectedDaysValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    return isSelectedDaysValidator(formGroup, this.isNBF, this.isRestore);
  }

  forceValid(data: ForceValidType = null): void {
    if (data.isNext && !this.confirmedDate && this.stepForm.get('ScheduleType').value === ScheduleType[ScheduleType.once]) {
      this.showConfirmIfNeed(data.isNext, data.isSave);
    }

    const needShowValidMessage =
      this.isNBF && !this.isRestore && !this.stepForm.valid && this.stepForm.errors && this.stepForm.errors.needSelectDay;

    if (needShowValidMessage) {
      const modalSettings: ModalSettings = {
        header: {
          title: this.stepForm.errors.needSelectDay.isFFI ? 'Schedule Forever Forward Incremental Options' : 'Schedule Recurring Options'
        },
        footer: { okButton: { text: 'Ok' }, cancelButton: { show: false } }
      };
      const message = `In the settings of a <span class="text-warning">${this.stepForm.errors.needSelectDay.message}</span> schedule, at least one week day should be selected`;

      this.modalService.open(modalSettings, message).then().catch(noop);
    }

    if (this.fastNTFS && this.stepForm.get('ScheduleType').value === 'instantly' && !this.confirmedOffNTFS) {
      const modalSettings: ModalSettings = {
        header: { title: 'Disable Use Fast NTFS Scan' },
        footer: { okButton: { text: 'Yes', type: 'success' }, cancelButton: { text: 'No' } }
      };

      this.modalService
        .open(modalSettings, this.fastNTFSModalContent)
        .then((confirm) => {
          if (confirm) {
            this.confirmedOffNTFS = true;
            this.offFastNTFS.emit();
          }
        })
        .catch(noop);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  showConfirmIfNeed(needNextStep: boolean, needSave: boolean): void {
    if (this.confirmedDate) return;

    const modalSettings: ModalSettings = {
      header: { title: this.i18nextService.t('wizards:schedule_step_confirm_past_date_title') },
      footer: {
        okButton: { text: this.i18nextService.t('shared:buttons.yes') },
        cancelButton: { text: this.i18nextService.t('shared:buttons.no') }
      }
    };

    this.modalService
      .confirm(modalSettings, this.i18nextService.t('wizards:schedule_step_confirm_past_date_message'))
      .then(() => {
        this.confirmedDate = true;
        this.stepForm.updateValueAndValidity();

        if (needNextStep || needSave) this.nextStep.emit(needSave);
      })
      .catch(() => this.stepForm.updateValueAndValidity());
  }

  dateTimeChangeHandler(): void {
    if (!this.isNBF || this.isRestore) {
      const specificDate = this.stepForm?.get('specificDateGroup').value;

      if (specificDate?.time && specificDate?.date) {
        const dateFromSpecific = getISODateFromTimeAndDate(specificDate.time.toString(), specificDate.date);

        if (this.isRDMode) {
          this.confirmedDate = moment().diff(moment(dateFromSpecific, baseDateFormat), 'days') <= 0;
          return;
        }

        const currentDateTimeInAgent = moment
          .utc()
          .utcOffset(this.agentTimezone || 0)
          .format(baseDateFormat);
        this.confirmedDate = moment(currentDateTimeInAgent, baseDateFormat).isBefore(dateFromSpecific, 'minutes');
      }
    }
  }

  minPeriodAlertClickHandler(event: Event): void {
    if ((event.target as HTMLElement).classList.contains('special-action-open-retention')) this.selectStep.emit(this.retentionTitle);
  }

  stopAfterHoursChangeHandler(event): void {
    if (typeof event === 'number' && !isNaN(event)) this.stepForm.get('stopAfterMinutes').updateValueAndValidity();
  }

  updateForm(value: ScheduleStepValue): void {
    value.oldStepLogic = true;

    if (this.isCreate && (!this.isNBF || this.isRestore) && !this.isRDMode) {
      const date = moment.utc().utcOffset(this.agentTimezone).add(1, 'hours').set('minute', 0);
      value.specificDateGroup = { date: date.toDate(), time: date.format('HH:mm:ss') };
    }

    this.initialScheduleType = value.ScheduleType === 'predefined' ? 'incremental' : value.ScheduleType;
    this.stepForm.reset(value);

    if (this.isNBF && !this.isRestore) {
      StepsHelpers.updateDaysOfWeek(value.incrementalData.weeklyDayOfWeek, this.stepForm.get('incrementalData') as UntypedFormGroup);
      StepsHelpers.updateDaysOfWeek(value.fullBackupData.weeklyDayOfWeek, this.stepForm.get('fullBackupData') as UntypedFormGroup);
      this.setPanelTitle(value.incrementalData, 'incrementalData');
      this.setPanelTitle(value.fullBackupData, 'fullBackupData');
    }

    this.scheduleTypeChangeHandler(value.ScheduleType, false);
    this.stopAfterEnabledChangeHandler(value.StopAfterEnabled);
    this.overdueAfterSuccessChangeHandler(value.overdueAfterSuccessEnabled);
  }

  stopAfterEnabledChangeHandler(event): void {
    this.toggleFormControls(['stopAfterHours', 'stopAfterMinutes'], event);

    if (event) this.stepForm.get('stopAfterMinutes').markAllAsTouched();
  }

  overdueAfterSuccessChangeHandler(event): void {
    this.toggleFormControls(['overdueAfterSuccessAmount', 'overdueAfterSuccessPeriod'], event);

    if (!event && !this.stepForm.get('overdueAfterSuccessAmount').value && !this.stepForm.get('overdueAfterSuccessPeriod').value) {
      this.stepForm.get('overdueAfterSuccessAmount').setValue(7);
      this.stepForm.get('overdueAfterSuccessPeriod').setValue(1);
    }
  }

  enabledFullChangeHandler(event): void {
    if (event) this.openScheduleModal(null);
  }

  scheduleTypeChangeHandler(event: string, fromUI = true): void {
    const wasFFI = this.initialScheduleType === 'ffi';
    const finallyFn = () => {
      const specialEvent = event === 'predefined' || event === 'ffi';
      if (this.isNBF && !this.isRestore && fromUI && specialEvent) this.openScheduleModal(event === 'ffi' ? 'ffi' : 'incremental');
    };

    this.changeType.emit(event);

    if (event !== ScheduleType[ScheduleType.once]) this.confirmedDate = false;

    switch (event) {
      case 'instantly': {
        this.markRetentionRequired(false);
        this.stepForm.get('ForceMissedSchedule').disable();
        this.stepForm.get('StopAfterEnabled').disable();
        this.incrementalData.emit(null);

        return void finallyFn();
      }
      case 'noschedule': {
        this.markRetentionRequired(false);
        this.stepForm.get('ForceMissedSchedule').disable();
        this.stepForm.get('StopAfterEnabled').enable();
        if (this.isNBF && !this.isRestore) {
          this.stepForm.get('incrementalData').disable();
          this.stepForm.get('fullBackupData').disable();
        }
        this.incrementalData.emit(null);

        return void finallyFn();
      }
      case 'ffi': {
        this.markRetentionRequired(!wasFFI);
        this.stepForm.get('ForceMissedSchedule').enable();
        this.stepForm.get('StopAfterEnabled').enable();
        this.stepForm.get('fullBackupData').disable();
        this.stepForm.get('incrementalData').enable();
        return void finallyFn();
      }
      default: {
        this.markRetentionRequired(false);
        this.stepForm.get('ForceMissedSchedule').enable();
        this.stepForm.get('StopAfterEnabled').enable();
        if (this.isNBF && !this.isRestore) {
          this.stepForm.get('incrementalData').enable();
          this.stepForm.get('fullBackupData').enable();
        }
      }
    }

    return void finallyFn();
  }

  openScheduleModal(mode: null | 'incremental' | 'ffi' = 'incremental', isEdit = false): void {
    const settings = new ModalSettings();
    const isFFI = mode === 'ffi';
    const isIncremental = mode === 'incremental' || isFFI;
    const isNewSchedule = this.initialScheduleType !== mode && !isEdit;
    const formControl = this.stepForm.get(isIncremental ? 'incrementalData' : 'fullBackupData') as UntypedFormGroup;
    let value: IncrementalData | FullBackupData = formControl.value;

    if (isNewSchedule) {
      value = {
        ...new UntypedFormGroup(
          StepsHelpers[isIncremental ? 'getDefaultIncrementalSchedule' : 'getDefaultFullSchedule'](this.validVersionForSchedule)
        ).value,
        recurringPeriod: 'Daily',
        weeklyDayOfWeek: [...Array(7)].map(() => true)
      };
    }

    settings.data = {
      isIncremental,
      isUnix: this.isUnix && !this.isNetBuild,
      isFFI,
      validVersionForSchedule: this.validVersionForSchedule,
      value
    };

    if (!isIncremental) {
      const incData = this.stepForm.get('incrementalData').value;

      if (!this.isUnix || this.isNetBuild) {
        settings.data.value.occursAtTime = incData.dailyFreqPeriodOption === 'OccursAt' ? incData.occursAtTime : '00:00';
      } else if (isNewSchedule) {
        settings.data.value = { ...incData };
      }
    }

    if (isNewSchedule || !isIncremental) {
      formControl.get('recurringPeriod').setValue(settings.data.value.recurringPeriod);
      StepsHelpers.updateDaysOfWeek(settings.data.value.weeklyDayOfWeek, formControl);
      this.setPanelTitle(formControl.value, isIncremental ? 'incrementalData' : 'fullBackupData');
    }

    this.modalService
      .openCustom(ScheduleModalComponent, settings)
      .then((result: ScheduleModalData) => {
        if (result) {
          if (result.fullBackupData?.enabled === false) {
            result.fullBackupData.enabled = true;
          }

          const formData = this.stepForm.get(result.incrementalData ? 'incrementalData' : 'fullBackupData') as UntypedFormGroup;
          const resultData = result.incrementalData ? result.incrementalData : result.fullBackupData;

          formData.reset(resultData);
          this.updateFullIfOccursAt(!!result.incrementalData, resultData?.occursAtTime);
          StepsHelpers.updateDaysOfWeek(resultData.weeklyDayOfWeek, formData);
          this.setPanelTitle(formData.value, result.incrementalData ? 'incrementalData' : 'fullBackupData');

          if (isFFI) {
            this.incrementalData.emit(resultData);
          }
        }
      })
      .catch(noop);
  }

  updateFullIfOccursAt(isIncremental: boolean, time: string): void {
    if (isIncremental) {
      const full = this.stepForm.get('fullBackupData');

      full.get('occursAtTime').setValue(time || full.get('occursAtTime').value);
      this.setPanelTitle(full.value, 'fullBackupData');

      return;
    }

    if (this.isUnix && !this.isNetBuild) {
      const incrementalData = this.stepForm.get('incrementalData');

      incrementalData.get('occursAtTime').setValue(time);
      this.setPanelTitle(incrementalData.value, 'incrementalData');
    }
  }

  setPanelTitle(schedule, name: string): void {
    schedule.weeklyDayOfWeek = schedule.weeklyDayOfWeek.map((d) => !!d);
    const newSchedule = WizardBaseForPlanHelper.ToPaskalCase(schedule);
    if (newSchedule.RecurringPeriod === 'Daily') newSchedule.RecurringPeriod = 'Weekly';
    else if (newSchedule.Occurrence === WeekNumber['Day Of Month'] && !newSchedule.WeeklyDayOfWeek.some((d) => d))
      newSchedule.RecurringPeriod = 'DayOfMonth';
    newSchedule.RepeatEveryCount = schedule.repeatEveryCount || 1;
    this.schedulesTexts[name] = StepsHelpers.getStringByScheduleData(newSchedule, false);
  }

  /**
   * A step is required to visit if it is invalid,
   * or we are creating a new plan and
   * have not yet reached a certain step.
   *
   * I added a third condition to mark the step as required to show.
   * The retention step will be marked as required for a visit
   * if the state of the radio button is set to FFI.
   * @param {boolean} required
   * @private
   */
  private markRetentionRequired(required: boolean): void {
    this[required ? 'registerRequiredStep' : 'unregisterRequiredStep'].emit(this.retentionTitle);
  }

  // The NBF schedule step publishes the same; the retention logic of a destination + retention step shown next to this one (NBF
  // plans on Unix or older agents) takes the schedule mode and schedules from here
  private publishScheduleContext(): void {
    if (!this.isNBF || this.isRestore || this.isConsistencyCheck) return;

    const scheduleType = this.stepForm.get('ScheduleType').value;

    this.stepService.updateProperty('schedule', 'isSimpleMode', scheduleType === 'ffi');
    this.stepService.updateProperty('schedule', 'isAdvancedMode', scheduleType === 'predefined');
    this.stepService.updateProperty('schedule', 'isManualMode', scheduleType === 'noschedule');
    this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').getRawValue());
    this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').getRawValue());
  }

  private setSubtitleType(): void {
    if (this.isConsistencyCheck) {
      return void (this.subtitleByType = this.i18nextService.t('wizards:consistencyCheck.consistencyLabel').toLowerCase());
    }

    if (this.isRestore) this.subtitleByType = this.i18nextService.t('wizards:restore');
  }
}
